import { useRef, useCallback } from "react";
import { css } from "@emotion/react";

export function useSurfaceRipple<T extends HTMLElement = HTMLDivElement>() {
  const rippleContainerRef = useRef<T>(null);

  const createRipple = useCallback((event: React.MouseEvent) => {
    const container = rippleContainerRef.current;
    if (!container) {
      return;
    }
    const rect = container.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;
    const ripple = document.createElement("span");
    ripple.className = "ripple-effect";
    ripple.style.cssText = `
      position: absolute;
      border-radius: 50%;
      pointer-events: none;
      width: ${size}px;
      height: ${size}px;
      left: ${x}px;
      top: ${y}px;
      background: rgba(0,0,0,0.2);
      transform: scale(0);
      animation: ripple-animate 500ms cubic-bezier(0.4,0,0.2,1);
      z-index: 2;
    `;
    // Ensure the container has relative position and overflow hidden
    container.style.position = container.style.position || "relative";
    container.style.overflow = "hidden";
    container.appendChild(ripple);
    ripple.addEventListener("animationend", () => {
      ripple.remove();
    });
  }, []);

  const rippleStyles = css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
    border-radius: inherit;
    pointer-events: none;
    z-index: 1;
    .ripple-effect {
      position: absolute;
      border-radius: 50%;
      pointer-events: none;
      background: rgba(0, 0, 0, 0.12);
      transform: scale(0);
      animation: ripple-animate 500ms cubic-bezier(0.4, 0, 0.2, 1);
      z-index: 2;
    }
    @keyframes ripple-animate {
      to {
        opacity: 0;
        transform: scale(1);
      }
    }
  `;

  return { rippleContainerRef, createRipple, rippleStyles };
}
